import React from "react";
import OptionButtonGroup from "app/components/OptionButtonGroup";
import SwitchToggle from "app/components/OptionButtonGroup/SwitchToggle";
import CustomOpenCalendarOptions from "app/components/OptionButtonGroup/CustomOpenCalendarOptions";
import { parseTime } from "app/routes/Scheduling/utils";
import { useTranslation } from "react-i18next";
import BookingOptionSelector, { MinimalBookingOption } from 'app/routes/Scheduling/Availability/BookingOptionSelector';
import useSlotInterval from "app/utils/hooks/scheduling/useSlotInterval";

interface DailyProps {
  day: number;
  active: boolean;
  startTime: string;
  endTime: string;
  value: number;
  label: string;
  short: string;
}

interface ScheduleProps {
  id?: string;
  name: string;
  dailyTimeTables: DailyProps[];
  customPeriod: boolean;
  startDate: string | Date;
  endDate: string | Date;
  lessonRates?: MinimalBookingOption[] | null;
}

interface AvailabilityWithDatesProps {
  schedule: ScheduleProps;
  /**
   * Update one field of the schedule being edited.
   *
   * Deliberately (key, value) with no id: this component is rendered both for a
   * saved schedule (Availability.tsx) and for the unsaved one in the Add modal,
   * and it should not have to know which. The saved-schedule caller binds its
   * own id. This prop used to be `any`, and the two callers really did expect
   * different arities — which is how every call here silently started updating
   * the wrong thing.
   */
  setScheduleData: (key: string, value: unknown) => void;
  bookingOptions?: MinimalBookingOption[];
  /** One day button (by weekday value) to expose for a coachmark anchor. */
  anchorDay?: number;
  anchorRef?: React.RefObject<HTMLButtonElement>;
  /** The whole day row, so any day button dismisses that coachmark. */
  daysRef?: React.RefObject<HTMLDivElement>;
}

const AvailabilityWithDates = ({
  schedule,
  setScheduleData,
  bookingOptions,
  anchorDay,
  anchorRef,
  daysRef,
}: AvailabilityWithDatesProps) => {
  const { t } = useTranslation();
  const { step } = useSlotInterval();

  const onDaySelect = (selectedDay: number) => {
    const updatedDaily = schedule.dailyTimeTables.map((daily) => {
      if (selectedDay !== daily.day) {
        return daily;
      }

      daily.active = !daily.active;

      if (daily.active && (!daily.startTime || !daily.endTime)) {
        daily.startTime = '08:00';
        daily.endTime = '17:00';
      }

      return daily;
    });
    setScheduleData('dailyTimeTables', updatedDaily);
  };

  const onHourChange = (selectedDay: number, time: any, type: string) => {
    const updatedDaily = schedule.dailyTimeTables.map((daily) => {
      if (selectedDay !== daily.day) {
        return daily;
      }
      daily[type] = time.value;
      daily['beginAt'] = parseTime(daily['startTime']);
      daily['endAt'] = parseTime(daily['endTime']);
      return daily;
    });
    setScheduleData('dailyTimeTables', updatedDaily);
  };

  return (
    <div>
      <label>{t("Days Available")}</label>
      <OptionButtonGroup
        options={schedule.dailyTimeTables}
        selected={schedule.dailyTimeTables.filter((oh) => oh.active)}
        setSelected={onDaySelect}
        onChange={onHourChange}
        shortButtons={true}
        step={step}
        anchorDay={anchorDay}
        anchorRef={anchorRef}
        groupRef={daysRef}
      />
      <SwitchToggle
        checked={schedule.customPeriod}
        onChange={(value: boolean) => {
          setScheduleData('customPeriod', value);
        }}
        title={t('This availability is active during a specific part of the year')}
        data-testid="custom-period-toggle"
      />
      {schedule.customPeriod && (
        <CustomOpenCalendarOptions
          setFromDate={(date: Date) => {
            setScheduleData('startDate', date);
          }}
          setToDate={(date: Date) => {
            setScheduleData('endDate', date);
          }}
          schedule={schedule}
        />
      )}

      <BookingOptionSelector
        value={schedule.lessonRates || []}
        bookingOptions={bookingOptions || []}
        onChange={(bookingOptions: MinimalBookingOption[]) => {
          setScheduleData('lessonRates', bookingOptions);
        }}
      />
    </div>
  );
};

export default AvailabilityWithDates;
